import React from 'react';

/**
 * Hero.jsx — beige-theme redesign.
 * Self-contained: the cute flat robot mascot, animations, and responsive
 * layout all live in this one file (the <style> block at the bottom).
 * Replaces the old 3D AIReceptionist hero. Drop in over the existing
 * frontend/src/components/Hero.jsx — no other files need changing.
 */
const Hero = () => {
  const scrollToBooking = () => {
    const el = document.getElementById('booking');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };
  const scrollToProducts = () => {
    const el = document.getElementById('use-cases');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="hero-cai">
      <div className="hero-cai-inner">
        {/* ── Text ── */}
        <div className="hero-cai-text">
          <div className="hero-cai-badge">
            <span className="hero-cai-badge-dot" />
            AI AUTOMATION FOR YOUR BUSINESS
          </div>
          <h1 className="hero-cai-title">
            Automate operations.{' '}
            <span className="hero-cai-underline">Reduce overhead.</span>{' '}
            Scale output.
          </h1>
          <p className="hero-cai-sub">
            We audit your workflows, identify inefficiencies, and deploy managed
            automation that reduces cost and improves throughput.
          </p>
          <div className="hero-cai-actions">
            <button className="hero-cai-btn-primary" onClick={scrollToBooking}>
              Schedule an Audit →
            </button>
            <button className="hero-cai-btn-ghost" onClick={scrollToProducts}>
              View capabilities
            </button>
          </div>
        </div>

        {/* ── Cute flat mascot ── */}
        <div className="hero-cai-mascot-wrap">
          <div className="hero-cai-mascot">
            <div className="hero-cai-antenna">
              <span className="hero-cai-antenna-rod" />
              <span className="hero-cai-antenna-tip" />
            </div>
            <div className="hero-cai-head">
              <div className="hero-cai-screen">
                <span className="hero-cai-eye" />
                <span className="hero-cai-eye" />
              </div>
              <span className="hero-cai-cheek hero-cai-cheek-l" />
              <span className="hero-cai-cheek hero-cai-cheek-r" />
              <span className="hero-cai-ear hero-cai-ear-l" />
              <span className="hero-cai-ear hero-cai-ear-r" />
              <span className="hero-cai-wave" role="img" aria-label="waving">👋</span>
            </div>
            <div className="hero-cai-neck" />
          </div>
          <div className="hero-cai-shadow" />
        </div>
      </div>

      {/* Scoped styles — keeps this a single drop-in file */}
      <style>{`
        .hero-cai {
          background: #ECEAE3;
          padding: 124px 40px 64px;
          background-image:
            linear-gradient(rgba(0,0,0,0.022) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,0,0,0.022) 1px, transparent 1px);
          background-size: 44px 44px;
        }
        .hero-cai-inner {
          max-width: 1200px; margin: 0 auto;
          display: grid; grid-template-columns: 1.12fr 0.88fr;
          gap: 48px; align-items: center;
        }
        .hero-cai-text { text-align: left; }
        .hero-cai-badge {
          display: inline-flex; align-items: center; gap: 9px;
          background: #fff; border-radius: 999px; padding: 10px 18px;
          font-size: 12px; font-weight: 700; letter-spacing: 0.07em; color: #1A1A18;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }
        .hero-cai-badge-dot { width: 8px; height: 8px; border-radius: 50%; background: #5E6B39; }
        .hero-cai-title {
          font-size: 60px; line-height: 1.04; font-weight: 800; letter-spacing: -0.03em;
          color: #1A1A18; margin: 24px 0 0;
          -webkit-text-fill-color: #1A1A18; background: none;
        }
        .hero-cai-underline { border-bottom: 6px solid #C0492A; padding-bottom: 2px; }
        .hero-cai-sub {
          font-size: 20px; line-height: 1.5; color: #5A564D; max-width: 580px; margin: 26px 0 0;
        }
        .hero-cai-actions { display: flex; gap: 14px; margin-top: 34px; flex-wrap: wrap; }
        .hero-cai-btn-primary {
          border: none; cursor: pointer; background: #C0492A; color: #fff;
          font-family: inherit; font-size: 16px; font-weight: 700; padding: 16px 28px;
          border-radius: 12px; transition: background .2s;
        }
        .hero-cai-btn-primary:hover { background: #A53D22; }
        .hero-cai-btn-ghost {
          border: 1.5px solid #1A1A18; cursor: pointer; background: transparent; color: #1A1A18;
          font-family: inherit; font-size: 16px; font-weight: 700; padding: 16px 28px;
          border-radius: 12px; transition: all .2s;
        }
        .hero-cai-btn-ghost:hover { background: #1A1A18; color: #fff; }

        /* mascot */
        .hero-cai-mascot-wrap { display: flex; flex-direction: column; align-items: center; justify-content: center; }
        .hero-cai-mascot { position: relative; animation: heroCaiFloat 4s ease-in-out infinite; }
        .hero-cai-antenna {
          position: absolute; top: -26px; left: 50%; margin-left: -1.5px;
          transform-origin: bottom center; animation: heroCaiAntenna 3s ease-in-out infinite;
        }
        .hero-cai-antenna-rod { display: block; width: 3px; height: 26px; background: #1A1A18; border-radius: 2px; }
        .hero-cai-antenna-tip {
          display: block; width: 14px; height: 14px; border-radius: 50%; background: #C0492A;
          margin: -4px 0 0 -5.5px; animation: heroCaiPulse 2s ease-in-out infinite;
        }
        .hero-cai-head {
          width: 220px; height: 188px; background: #fff; border-radius: 40px;
          box-shadow: 0 18px 40px rgba(0,0,0,0.10); border: 3px solid #1A1A18;
          position: relative; display: flex; align-items: center; justify-content: center;
        }
        .hero-cai-screen {
          width: 176px; height: 126px; background: #1A1A18; border-radius: 28px;
          display: flex; align-items: center; justify-content: center; gap: 32px;
        }
        .hero-cai-eye {
          width: 27px; height: 36px; background: #5E6B39; border-radius: 14px;
          transform-origin: center; animation: heroCaiBlink 4s ease-in-out infinite;
        }
        .hero-cai-cheek { position: absolute; bottom: 36px; width: 16px; height: 9px; border-radius: 50%; background: rgba(192,73,42,0.35); }
        .hero-cai-cheek-l { left: 24px; } .hero-cai-cheek-r { right: 24px; }
        .hero-cai-ear { position: absolute; top: 68px; width: 14px; height: 42px; background: #C0492A; border-radius: 7px; border: 3px solid #1A1A18; }
        .hero-cai-ear-l { left: -13px; } .hero-cai-ear-r { right: -13px; }
        .hero-cai-wave {
          position: absolute; right: -36px; top: 32px; font-size: 36px;
          transform-origin: bottom left; animation: heroCaiWave 3.5s ease-in-out infinite;
        }
        .hero-cai-neck {
          width: 74px; height: 16px; background: #fff; border: 3px solid #1A1A18;
          border-top: none; border-radius: 0 0 14px 14px; margin: -2px auto 0;
        }
        .hero-cai-shadow {
          width: 158px; height: 18px; border-radius: 50%; background: #1A1A18;
          margin-top: 24px; opacity: 0.16; animation: heroCaiShadow 4s ease-in-out infinite;
        }

        @keyframes heroCaiFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-14px); } }
        @keyframes heroCaiBlink { 0%,92%,100% { transform: scaleY(1); } 96% { transform: scaleY(0.1); } }
        @keyframes heroCaiAntenna { 0%,100% { transform: rotate(-6deg); } 50% { transform: rotate(6deg); } }
        @keyframes heroCaiPulse { 0%,100% { opacity: 1; box-shadow: 0 0 0 0 rgba(192,73,42,0.5); } 50% { opacity: 0.7; box-shadow: 0 0 0 8px rgba(192,73,42,0); } }
        @keyframes heroCaiWave { 0%,60%,100% { transform: rotate(8deg); } 70% { transform: rotate(-22deg); } 80% { transform: rotate(8deg); } 90% { transform: rotate(-22deg); } }
        @keyframes heroCaiShadow { 0%,100% { transform: scaleX(1); opacity: 0.16; } 50% { transform: scaleX(0.8); opacity: 0.09; } }

        @media (max-width: 860px) {
          .hero-cai { padding: 104px 22px 48px; }
          .hero-cai-inner { grid-template-columns: 1fr; gap: 36px; }
          .hero-cai-mascot-wrap { order: -1; }
          .hero-cai-title { font-size: 40px; }
          .hero-cai-sub { font-size: 17px; }
        }
      `}</style>
    </section>
  );
};

export default Hero;
